import React, { memo, useEffect, useRef, useState } from 'react';
import { Liquid } from 'liquidjs';
import { BeforeMount, Editor, OnMount } from '@monaco-editor/react';
import { Button, Flex, Modal, Text } from '@mantine/core';
import { useForm, useFormContext } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { useClipboard } from '@mantine/hooks';
import {
	CustomDetailDefinitionSource,
	MONACO_EDITOR_SOURCES_MODEL_DECLARATION,
	MONACO_EDITOR_SOURCES_TYPE_DECLARATION,
} from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { isEmpty } from 'lodash';
import {
	initializeLiquidJsEngine,
	LiquidJsCustomFilters,
} from '@dealstack/shared/lib/common/liquidJs/liquidJs.ts';
import {
	convertArrayPathLiquidJsCode,
	getLiquidJsFiltersSuggestions,
	SUGGESTIONS_SETTINGS,
	TS_ERRORS_TO_IGNORE,
} from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/monacoEditorSettings.ts';
import { TextInput } from 'react-hook-form-mantine';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue.ts';
import {
	SMART_FIELDS_SOURCES_MOCKED_DATA,
	SmartFieldsAvailableSources,
} from '@dealstack/shared/lib/common/business/smartFields.ts';

enum EDIT_FIELD_MAPPING_FIELDS {
	SOURCE_TEMPLATE = 'sourceTemplate',
	SOURCE_DESCRIPTION = 'sourceDescription',
}

interface EditFieldMappingForm {
	[EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE]: string;
	[EDIT_FIELD_MAPPING_FIELDS.SOURCE_DESCRIPTION]: string;
}

interface Props {
	onClose: () => void;
	opened: boolean;
	fieldKey?: string;
	fieldPath: string;
	customDetailsDefinitionsSources?: CustomDetailDefinitionSource[];
}

const schema = Yup.object().shape({
	sourceTemplate: Yup.string(),
	sourceDescription: Yup.string().when(
		EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE,
		([sourceTemplate], schema) =>
			isEmpty(sourceTemplate) ? schema : schema.required('Cannot be empty if source provided')
	),
});

const EditFieldMappingModal = ({
	opened,
	onClose,
	fieldKey,
	fieldPath,
	customDetailsDefinitionsSources,
}: Props) => {
	const clipboard = useClipboard();
	const mainFormContext = useFormContext();

	const editFieldMappingForm = useForm<EditFieldMappingForm>({
		mode: 'all',
		resolver: yupResolver(schema),
		defaultValues: {
			sourceTemplate: mainFormContext.getValues(`${fieldPath}.fieldSourceTemplate`) ?? '',
			sourceDescription:
				mainFormContext.getValues(`${fieldPath}.fieldSourceDescription`) ?? '',
		},
	});

	useEffect(() => {
		editFieldMappingForm.reset({
			sourceTemplate: mainFormContext.getValues(`${fieldPath}.fieldSourceTemplate`) ?? '',
			sourceDescription:
				mainFormContext.getValues(`${fieldPath}.fieldSourceDescription`) ?? '',
		});
	}, [fieldKey]);

	const onConfirm = ({ sourceTemplate, sourceDescription }: EditFieldMappingForm) => {
		const sourceTemplateToSet = isEmpty(sourceTemplate) ? null : sourceTemplate;
		mainFormContext.setValue(`${fieldPath}.fieldSourceTemplate`, sourceTemplateToSet);

		const sourceDescriptionToSet =
			isEmpty(sourceDescription) || isEmpty(sourceTemplate) ? null : sourceDescription;
		mainFormContext.setValue(`${fieldPath}.fieldSourceDescription`, sourceDescriptionToSet);

		onClose();
	};

	const onReset = () => {
		mainFormContext.setValue(`${fieldPath}.fieldSourceTemplate`, null);
		mainFormContext.setValue(`${fieldPath}.fieldSourceDescription`, null);

		onClose();
	};

	const [availableData, setAvailableData] = useState<SmartFieldsAvailableSources>(
		SMART_FIELDS_SOURCES_MOCKED_DATA
	);

	useEffect(
		() =>
			setAvailableData((prevData) => ({
				...prevData,
				customDetails: customDetailsDefinitionsSources ?? [],
			})),
		[customDetailsDefinitionsSources]
	);

	const [output, setOutput] = useState<string>('');
	const [error, setError] = useState<string | null>(null);

	const liquidEngineRef = useRef<Liquid | null>(null);
	if (!liquidEngineRef.current) {
		liquidEngineRef.current = initializeLiquidJsEngine();
	}

	const debouncedSourceTemplate = useDebounceValue(
		editFieldMappingForm.watch(EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE),
		500
	);

	useEffect(() => {
		if (!liquidEngineRef.current) {
			return;
		}

		const sourceTemplate = editFieldMappingForm.getValues(
			EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE
		);

		if (isEmpty(sourceTemplate)) {
			setOutput('');
			setError(null);
			return;
		}

		liquidEngineRef.current
			.parseAndRender(sourceTemplate as string, { data: availableData })
			.then((result) => {
				setOutput(result);
				setError(null);
			})
			.catch((err) => {
				setError(err.message);
				setOutput('');
			});
	}, [debouncedSourceTemplate, availableData]);

	const handleEditorWillMount: BeforeMount = (monacoInstance) => {
		//these errors appears for valid liquidJs code, so we have to ignore them
		monacoInstance.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
			diagnosticCodesToIgnore: TS_ERRORS_TO_IGNORE,
		});

		//adding sources types for monaco to enable suggestions
		monacoInstance.languages.typescript.typescriptDefaults.addExtraLib(
			MONACO_EDITOR_SOURCES_TYPE_DECLARATION
		);
	};

	const handleEditorDidMount: OnMount = (_editor, monacoInstance) => {
		const modelUri = monacoInstance.Uri.parse('inmemory://model/context.d.ts');
		if (!monacoInstance.editor.getModel(modelUri)) {
			//adding sources model for monaco to enable suggestions
			monacoInstance.editor.createModel(
				MONACO_EDITOR_SOURCES_MODEL_DECLARATION,
				'typescript',
				modelUri
			);
		}

		//adding suggestion for liquidJs filters
		monacoInstance.languages.registerCompletionItemProvider('typescript', {
			provideCompletionItems: (model, position) => {
				const word = model.getWordUntilPosition(position);
				const range = {
					startLineNumber: position.lineNumber,
					endLineNumber: position.lineNumber,
					startColumn: word.startColumn,
					endColumn: word.endColumn,
				};

				return { suggestions: getLiquidJsFiltersSuggestions(range) };
			},
		});
	};

	const onPropertyCopy = (e) => {
		if (isEmpty(availableData.customDetails) || !e.namespace.includes('customDetails')) {
			clipboard.copy(convertArrayPathLiquidJsCode(e.namespace));
			return;
		}

		// Use the third element in 'e.namespace' as the customDetailIndex
		const customDetailIndex = e.namespace[2] ?? 0;
		const customDetailId = availableData.customDetails?.[customDetailIndex]?.id ?? '';

		clipboard.copy(
			`{{data.customDetails | ${LiquidJsCustomFilters.GET_CUSTOM_DETAIL_VALUE}: "${customDetailId}"}}`
		);
	};

	const showResetFieldButton =
		!!editFieldMappingForm.watch(EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE) ||
		!!editFieldMappingForm.watch(EDIT_FIELD_MAPPING_FIELDS.SOURCE_DESCRIPTION);

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Edit field source"
			size="80vw"
			centered
			trapFocus={false}
		>
			<Flex gap="2rem" align="flex-start" mb="1.5rem">
				<Flex gap="0.5rem" align="center" w="60%">
					<Text size="B5Medium">Field key:</Text>
					<Text size="B5Bold">{fieldKey}</Text>
				</Flex>

				<Flex w="100%">
					<TextInput
						name={EDIT_FIELD_MAPPING_FIELDS.SOURCE_DESCRIPTION}
						control={editFieldMappingForm.control}
						label="Field source description"
						placeholder="Provide short source description"
						size="md"
						w="30rem"
					/>
				</Flex>
			</Flex>

			<Flex gap="2rem">
				<Flex gap="1rem" direction="column" w="60%">
					<Text size="B5Medium">Available data</Text>

					<ReactJson
						theme="monokai"
						onEdit={(e) =>
							setAvailableData(e.updated_src as SmartFieldsAvailableSources)
						}
						src={availableData}
						enableClipboard={onPropertyCopy}
						displayDataTypes={false}
						name="data"
						displayObjectSize={false}
						style={{
							padding: '1rem',
							overflowY: 'auto',
							maxHeight: '40rem',
						}}
					/>
				</Flex>

				<Flex gap="1rem" direction="column" sx={{ overflow: 'hidden' }} w="100%">
					<Text size="B5Medium">Code editor</Text>
					<Editor
						height="20rem"
						theme="vs-dark"
						defaultLanguage="typescript"
						value={editFieldMappingForm.watch(
							EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE
						)}
						onChange={(value: string) => {
							editFieldMappingForm.setValue(
								EDIT_FIELD_MAPPING_FIELDS.SOURCE_TEMPLATE,
								value
							);
						}}
						beforeMount={handleEditorWillMount}
						onMount={handleEditorDidMount}
						options={{
							minimap: { enabled: false },
							lineNumbersMinChars: 2,
							overviewRulerLanes: 0,
							glyphMargin: false,
							wordWrap: 'on',
							padding: {
								top: 8,
								bottom: 8,
							},
							suggest: SUGGESTIONS_SETTINGS,
						}}
					/>

					<Text size="B5Medium">Result value</Text>
					<div
						style={{
							minHeight: '5rem',
							padding: '1rem',
							background: defaultTheme.v3.colors.gray[2],
							whiteSpace: 'pre-wrap',
						}}
					>
						{error ? <span style={{ color: 'red' }}>{error}</span> : output}
					</div>
				</Flex>
			</Flex>
			<Flex justify="right" mt="1rem" gap="1rem">
				{showResetFieldButton && (
					<Button variant="deletion" onClick={onReset}>
						Remove field mapping
					</Button>
				)}
				<Button variant="primary" onClick={editFieldMappingForm.handleSubmit(onConfirm)}>
					Confirm
				</Button>
			</Flex>
		</Modal>
	);
};
const _EditFieldMappingModalExport = memo(EditFieldMappingModal);

export { _EditFieldMappingModalExport as EditFieldMappingModal };
